<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import subDays from 'date-fns/subDays';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';

const { t } = useI18n();
const store = useStore();

const uiFlags = useMapGetter('getOverviewUIFlags');
const isExporting = computed(() => uiFlags.value?.isExportingOverviewReport);

const customDateRange = ref([subDays(new Date(), 6), new Date()]);
const selectedDateRange = ref(DATE_RANGE_TYPES.LAST_7_DAYS);

const from = computed(() => getUnixStartOfDay(customDateRange.value[0]));
const to = computed(() => getUnixEndOfDay(customDateRange.value[1]));

const onDateRangeChange = value => {
  const [startDate, endDate, rangeType] = value;
  customDateRange.value = [startDate, endDate];
  selectedDateRange.value = rangeType || DATE_RANGE_TYPES.CUSTOM_RANGE;
};

const downloadOverviewExport = async () => {
  try {
    await store.dispatch('downloadOverviewExport', {
      from: from.value,
      to: to.value,
    });
  } catch {
    useAlert(t('OVERVIEW_REPORTS.EXPORT.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <section
    class="flex flex-col gap-4 rounded-xl bg-n-solid-2 px-6 py-5 shadow outline outline-1 outline-n-container"
  >
    <div class="flex flex-col gap-2">
      <h6 class="text-base font-medium text-n-slate-12">
        {{ t('OVERVIEW_REPORTS.EXPORT.TITLE') }}
      </h6>
      <p class="text-sm text-n-slate-11">
        {{ t('OVERVIEW_REPORTS.EXPORT.DESCRIPTION') }}
      </p>
    </div>
    <div
      class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between"
    >
      <WootDatePicker
        v-model:date-range="customDateRange"
        v-model:range-type="selectedDateRange"
        @date-range-changed="onDateRangeChange"
      />
      <Button
        size="sm"
        :label="t('OVERVIEW_REPORTS.EXPORT.DOWNLOAD')"
        :is-loading="isExporting"
        :disabled="isExporting"
        @click="downloadOverviewExport"
      />
    </div>
  </section>
</template>
